import React, { useEffect, useRef } from 'react';
import Alert from '../../../Alert';
import { validateCanadianPostalCode } from '../../../../helpers/canadianLocation';
import niceNameForShippingMethod from '../niceNameForShippingMethod';
import { getAllowedShippingMethods } from '../shippingMethodOptions';

const COUNTRY_NAMES = {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    AU: 'Australia',
    NZ: 'New Zealand',
    IE: 'Ireland',
};

const modeDescriptions = {
    none: 'No separate shipping fee. Shipping cost is included in the product price.',
    flat: 'Flat shipping rates are shown before checkout.',
    calculated: 'Shipping is calculated at checkout from the buyer postal code.',
    pickup: 'Buyers pick up the order in person.',
};

const formatWeight = (state) => {
    const numeric = parseFloat(state.weight);
    if (Number.isNaN(numeric) || numeric <= 0) return 'Not set';
    if (state.weightUnit === 'oz') {
        return `${Number((numeric * 16).toFixed(2))} oz`;
    }
    return `${numeric} lbs`;
};

export function getShippingIssues(shipping, { shipToCountries = [] } = {}) {
    const issues = [];
    const mode = shipping?.mode || 'none';

    if (mode === 'none') return issues;

    if (!shipping.originZIPCode || String(shipping.originZIPCode).trim() === '') {
        issues.push(
            shipping.originCountry === 'CA'
                ? 'Set your origin postal code.'
                : 'Set your origin ZIP code.',
        );
    } else if (
        shipping.originCountry === 'CA'
        && !validateCanadianPostalCode(shipping.originZIPCode)
    ) {
        issues.push('Enter a valid Canadian postal code in A1A 1A1 format.');
    }

    if (mode === 'pickup') {
        if (!shipping.pickupAddress || shipping.pickupAddress.trim() === '') {
            issues.push('Set your pickup address.');
        }
        return issues;
    }

    if (!shipping.methods || shipping.methods.length === 0) {
        issues.push('Select at least one shipping method.');
    } else if (shipping.originCountry === 'CA') {
        const allowedMethods = new Set(getAllowedShippingMethods({
            originCountry: shipping.originCountry,
            shipToCountries,
            mode: shipping.mode,
        }));
        if (shipping.methods.some(method => !allowedMethods.has(method))) {
            issues.push('Select Canada Post services that match this product\'s destinations.');
        }
    }

    if (!shipping.weight || parseFloat(shipping.weight) <= 0) {
        issues.push('Set a valid weight for your product.');
    }

    if (!shipping.length || parseFloat(shipping.length) <= 0) {
        issues.push('Set a valid package length.');
    }

    if (!shipping.width || parseFloat(shipping.width) <= 0) {
        issues.push('Set a valid package width.');
    }

    if (!shipping.height || parseFloat(shipping.height) <= 0) {
        issues.push('Set a valid package height.');
    }

    if (mode === 'flat') {
        const selectedMethods = (shipping.methods || []).filter((method) => method !== 'LOCAL_PICKUP');
        const rates = shipping.rates || {};
        selectedMethods.forEach((method) => {
            if (!rates[method] || parseFloat(rates[method]) <= 0) {
                issues.push(`Missing rate for ${niceNameForShippingMethod(method)}.`);
            }
        });
    }

    if ((shipping.methods || []).includes('LOCAL_PICKUP') && (!shipping.pickupAddress || shipping.pickupAddress.trim() === '')) {
        issues.push('Set your pickup address for local pickup.');
    }

    if (shipping.allowCombinedShipping && (shipping.secondaryRate === '' || parseFloat(shipping.secondaryRate) < 0)) {
        issues.push('Set a valid secondary item rate.');
    }

    return issues;
}

function CalculatedExamplesTable({ examples }) {
    return (
        <table className="shipping-examples-table">
            <thead>
                <tr>
                    <th>From Postal Code</th>
                    <th>To Postal Code</th>
                    <th>Shipping Method</th>
                    <th>Rate</th>
                </tr>
            </thead>
            <tbody>
                {examples.map((example, index) => (
                    <tr key={`${example.method}-${example.destinationZIPCode}-${index}`}>
                        <td data-label="From Postal Code">
                            {example.input?.originZIPCode || ''}
                        </td>
                        <td data-label="To Postal Code">{example.destinationZIPCode}</td>
                        <td data-label="Shipping Method">{niceNameForShippingMethod(example.method)}</td>
                        <td data-label="Rate">${example.rate ? Number(example.rate).toFixed(2) : '0.00'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function SummaryStep({
    state,
    examples = [],
    examplesLoading = false,
    examplesError = '',
    fetchExamples,
    shipToCountries = [],
}) {
    const issues = getShippingIssues(state, { shipToCountries });
    const methods = state.mode === 'pickup' ? ['LOCAL_PICKUP'] : (state.methods || []);
    const needsAddOnPickupAddress = state.mode !== 'pickup' && methods.includes('LOCAL_PICKUP');
    const requestedExamples = useRef(false);

    useEffect(() => {
        if (state.mode !== 'calculated') {
            requestedExamples.current = false;
            return;
        }

        if (fetchExamples && !requestedExamples.current && !examplesLoading && examples.length === 0) {
            requestedExamples.current = true;
            fetchExamples();
        }
    }, [state.mode, fetchExamples, examplesLoading, examples.length]);

    return (
        <div className="shipping-step">
            <h3 style={{ marginBottom: '2px' }}>Review Shipping Settings</h3>
            <p className="help" style={{ marginBottom: '1rem' }}>Click any step above to jump back to it.</p>

            {issues.length > 0 && (
                <Alert type="error">
                    <p style={{ marginBottom: '1rem' }}><strong>We found some issues with your shipping settings:</strong></p>
                    <ul>
                        {issues.map((issue) => <li key={issue}>{issue}</li>)}
                    </ul>
                </Alert>
            )}

            <dl className="shipping-summary">
                <dt>Mode</dt>
                <dd>{modeDescriptions[state.mode || 'none']}</dd>

                {state.mode !== 'none' && (
                    <>
                        <dt>Origin</dt>
                        <dd>
                            {COUNTRY_NAMES[state.originCountry] || state.originCountry}
                            {' · '}
                            {state.originZIPCode || 'Not set'}
                        </dd>
                    </>
                )}

                {state.mode !== 'none' && state.mode !== 'pickup' && (
                    <>
                        <dt>Package</dt>
                        <dd>
                            {(state.length && state.width && state.height)
                                ? `${state.length} x ${state.width} x ${state.height}" - ${formatWeight(state)}`
                                : 'Not set'}
                        </dd>

                        <dt>Methods</dt>
                        <dd>
                            {methods.length
                                ? methods.map((method) => niceNameForShippingMethod(method)).join(', ')
                                : 'Not set'}
                        </dd>

                        <dt>Rates</dt>
                        <dd>
                            {state.mode === 'calculated'
                                ? 'Calculated at checkout'
                                : methods.filter((method) => method !== 'LOCAL_PICKUP').map((method) => (
                                    `${niceNameForShippingMethod(method)}: $${Number(state.rates?.[method] || 0).toFixed(2)}`
                                )).join(', ') || 'Not set'}
                        </dd>

                        <dt>Combined Shipping</dt>
                        <dd>
                            {state.allowCombinedShipping
                                ? `Yes, $${Number(state.secondaryRate || 0).toFixed(2)} per additional item`
                                : 'No'}
                        </dd>
                    </>
                )}

                {(state.mode === 'pickup' || needsAddOnPickupAddress) && (
                    <>
                        <dt>Pickup Address</dt>
                        <dd>{state.pickupAddress || 'Not set'}</dd>
                    </>
                )}
            </dl>

            {state.mode === 'calculated' && (
                <div className="group">
                    <h4>Calculated Rate Examples</h4>
                    {examplesLoading && (
                        <p className="help">
                            {state.originCountry === 'CA'
                                ? 'Looking up rates from Canada Post...'
                                : 'Looking up rates from USPS and UPS...'}
                        </p>
                    )}
                    {examplesError && (
                        <Alert type="error">
                            <p>{examplesError}</p>
                            {fetchExamples && (
                                <button type="button" className="button" onClick={fetchExamples}>
                                    Retry
                                </button>
                            )}
                        </Alert>
                    )}
                    {!examplesLoading && !examplesError && examples.length === 0 && (
                        <p className="help">No example rates are available yet.</p>
                    )}
                    {examples.length > 0 && <CalculatedExamplesTable examples={examples} />}
                </div>
            )}
        </div>
    );
}
